import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, formatApiError, authRequest } from "@/lib/auth";
import {
  Wrench,
  LogOut,
  Calendar,
  MessageSquare,
  UserPlus,
  Phone,
  Mail,
  Car,
  RefreshCcw,
  Check,
  X,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleUnauthorized = useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m, l] = await Promise.all([
        authRequest.get(`${API}/appointments`),
        authRequest.get(`${API}/contact`),
        authRequest.get(`${API}/leads`),
      ]);
      setAppointments(a.data);
      setMessages(m.data);
      setLeads(l.data);
    } catch (e) {
      toast.error("Failed to load data", {
        description: formatApiError(e?.response?.data?.detail) || e.message,
      });
      if (e?.response?.status === 401) {
        handleUnauthorized();
      }
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  const updateApptStatus = async (id, newStatus) => {
    try {
      await authRequest.patch(`${API}/appointments/${id}`, { status: newStatus });
      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: newStatus } : a))
      );
      toast.success(`Marked ${newStatus}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const deleteRecord = async (kind, id, label) => {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      await authRequest.delete(`${API}/${kind}/${id}`);
      if (kind === "appointments") {
        setAppointments((prev) => prev.filter((x) => x.id !== id));
      } else if (kind === "contact") {
        setMessages((prev) => prev.filter((x) => x.id !== id));
      } else if (kind === "leads") {
        setLeads((prev) => prev.filter((x) => x.id !== id));
      }
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} deleted`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const counts = useMemo(
    () => ({
      new: appointments.filter((a) => a.status === "new").length,
      confirmed: appointments.filter((a) => a.status === "confirmed").length,
      completed: appointments.filter((a) => a.status === "completed").length,
      messages: messages.length,
      leads: leads.length,
    }),
    [appointments, messages, leads]
  );

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#0B132B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AdminHeader
        email={user?.email}
        onRefresh={fetchAll}
        onLogout={onLogout}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <KPIGrid counts={counts} />

        <Tabs defaultValue="appointments">
          <TabsList className="bg-white border border-[#E2E8F0] rounded-none p-0 h-auto">
            <AdminTabTrigger value="appointments" icon={Calendar} label="Appointments" />
            <AdminTabTrigger value="messages" icon={MessageSquare} label="Messages" />
            <AdminTabTrigger value="leads" icon={UserPlus} label="Email Leads" />
          </TabsList>

          <TabsContent value="appointments" className="mt-6">
            <AppointmentsPanel
              loading={loading}
              appointments={appointments}
              onUpdate={updateApptStatus}
              onDelete={(id) => deleteRecord("appointments", id, "appointment")}
            />
          </TabsContent>
          <TabsContent value="messages" className="mt-6">
            <MessagesPanel
              loading={loading}
              messages={messages}
              onDelete={(id) => deleteRecord("contact", id, "message")}
            />
          </TabsContent>
          <TabsContent value="leads" className="mt-6">
            <LeadsPanel
              loading={loading}
              leads={leads}
              onDelete={(id) => deleteRecord("leads", id, "lead")}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ---------- Sub-components ----------

function AdminHeader({ email, onRefresh, onLogout }) {
  return (
    <header className="bg-[#0B132B] text-white border-b-2 border-[#D32F2F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-[#D32F2F] flex items-center justify-center">
            <Wrench className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold">VETERAN'S AUTO · ADMIN</div>
            <div className="font-mono-label text-[#D32F2F]">Signed in as {email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            data-testid="admin-refresh-btn"
            className="hidden md:inline-flex items-center gap-2 border-2 border-white/30 hover:bg-white hover:text-[#0B132B] px-4 py-2 rounded-none text-sm font-semibold transition-colors"
          >
            <RefreshCcw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 bg-[#D32F2F] hover:bg-[#B71C1C] px-4 py-2 rounded-none text-sm font-semibold"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>
    </header>
  );
}

function KPIGrid({ counts }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E2E8F0] border border-[#E2E8F0] mb-10">
      <KPI label="New Requests" value={counts.new} accent />
      <KPI label="Confirmed" value={counts.confirmed} />
      <KPI label="Completed" value={counts.completed} />
      <KPI label="Messages" value={counts.messages} />
      <KPI label="Email Leads" value={counts.leads} />
    </div>
  );
}

function AdminTabTrigger({ value, icon: Icon, label }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={`tab-${value}`}
      className="rounded-none data-[state=active]:bg-[#0B132B] data-[state=active]:text-white px-6 py-3 font-semibold"
    >
      <Icon className="w-4 h-4 mr-2" /> {label}
    </TabsTrigger>
  );
}

function AppointmentsPanel({ loading, appointments, onUpdate, onDelete }) {
  if (loading) return <LoadingBlock />;
  if (appointments.length === 0) return <EmptyBlock text="No appointment requests yet." />;
  return (
    <div className="space-y-3">
      {appointments.map((a) => (
        <AppointmentCard key={a.id} appt={a} onUpdate={onUpdate} onDelete={onDelete} />
      ))}
    </div>
  );
}

function MessagesPanel({ loading, messages, onDelete }) {
  if (loading) return <LoadingBlock />;
  if (messages.length === 0) return <EmptyBlock text="No contact messages yet." />;
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <MessageCard key={m.id} msg={m} onDelete={onDelete} />
      ))}
    </div>
  );
}

function LeadsPanel({ loading, leads, onDelete }) {
  if (loading) return <LoadingBlock />;
  if (leads.length === 0) return <EmptyBlock text="No email leads captured yet." />;
  return (
    <div className="bg-white border border-[#E2E8F0]">
      <table className="w-full text-left">
        <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
          <tr>
            <th className="font-mono-label text-[#0B132B] px-5 py-3">Email</th>
            <th className="font-mono-label text-[#0B132B] px-5 py-3">Source</th>
            <th className="font-mono-label text-[#0B132B] px-5 py-3">Captured</th>
            <th className="font-mono-label text-[#0B132B] px-5 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-b border-[#E2E8F0] last:border-0">
              <td className="px-5 py-3 font-semibold text-[#0B132B]">{l.email}</td>
              <td className="px-5 py-3 text-sm text-[#4A5568]">{l.source}</td>
              <td className="px-5 py-3 text-sm text-[#4A5568]">
                {formatDate(l.created_at)}
              </td>
              <td className="px-5 py-3 text-right">
                <button
                  onClick={() => onDelete(l.id)}
                  data-testid={`lead-delete-${l.id}`}
                  className="inline-flex items-center gap-1 text-[#4A5568] hover:text-[#D32F2F] text-xs font-semibold"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div className="bg-white p-5">
      <div className={`font-display text-3xl md:text-4xl font-bold ${accent ? "text-[#D32F2F]" : "text-[#0B132B]"}`}>
        {value}
      </div>
      <div className="mt-1 font-mono-label text-[#4A5568]">{label}</div>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="bg-white border border-[#E2E8F0] p-10 flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#D32F2F]" />
    </div>
  );
}

function EmptyBlock({ text }) {
  return (
    <div className="bg-white border border-[#E2E8F0] border-dashed p-10 text-center text-[#4A5568]">
      {text}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-[#D32F2F] text-white",
    confirmed: "bg-[#0B132B] text-white",
    completed: "bg-[#E2E8F0] text-[#0B132B]",
    cancelled: "bg-[#4A5568] text-white",
  };
  return (
    <span
      className={`font-mono-label px-2 py-1 ${map[status] || map.new}`}
      data-testid={`status-badge-${status}`}
    >
      {status}
    </span>
  );
}

function AppointmentCard({ appt, onUpdate }) {
  return (
    <article
      data-testid={`appt-card-${appt.id}`}
      className="bg-white border border-[#E2E8F0] border-l-4 border-l-[#D32F2F] p-5 md:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h3 className="font-display text-xl font-bold text-[#0B132B]">{appt.name}</h3>
            <StatusBadge status={appt.status} />
          </div>
          <div className="font-mono-label text-[#4A5568]">
            {formatDate(appt.created_at)}
            {appt.preferred_date && ` · Prefers ${appt.preferred_date}`}
          </div>
        </div>
        <AppointmentActions appt={appt} onUpdate={onUpdate} />
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <InfoRow icon={Phone} label="Phone" value={appt.phone} href={`tel:${appt.phone}`} />
        {appt.email && (
          <InfoRow icon={Mail} label="Email" value={appt.email} href={`mailto:${appt.email}`} />
        )}
        <InfoRow icon={Car} label="Vehicle" value={appt.vehicle} />
        <InfoRow icon={Wrench} label="Service" value={appt.service} />
      </div>

      {appt.message && (
        <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
          <div className="font-mono-label text-[#4A5568] mb-1">Message</div>
          <p className="text-[#1A202C] leading-relaxed">{appt.message}</p>
        </div>
      )}
    </article>
  );
}

function AppointmentActions({ appt, onUpdate }) {
  const canConfirm = appt.status !== "confirmed" && appt.status !== "completed";
  const canComplete = appt.status !== "completed";
  const canCancel = appt.status !== "cancelled";
  return (
    <div className="flex gap-2 flex-wrap">
      {canConfirm && (
        <ActionButton onClick={() => onUpdate(appt.id, "confirmed")} icon={Check} label="Confirm" tone="navy" />
      )}
      {canComplete && (
        <ActionButton onClick={() => onUpdate(appt.id, "completed")} icon={Check} label="Complete" tone="red" />
      )}
      {canCancel && (
        <ActionButton onClick={() => onUpdate(appt.id, "cancelled")} icon={X} label="Cancel" tone="ghost" />
      )}
    </div>
  );
}

function ActionButton({ onClick, icon: Icon, label, tone }) {
  const tones = {
    navy: "bg-[#0B132B] hover:bg-[#141e3c] text-white",
    red: "bg-[#D32F2F] hover:bg-[#B71C1C] text-white",
    ghost: "border border-[#E2E8F0] hover:bg-[#F8F9FA] text-[#4A5568]",
  };
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-none ${tones[tone]}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function MessageCard({ msg }) {
  return (
    <article
      data-testid={`msg-card-${msg.id}`}
      className="bg-white border border-[#E2E8F0] border-l-4 border-l-[#0B132B] p-5 md:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-xl font-bold text-[#0B132B]">{msg.name}</h3>
          <div className="font-mono-label text-[#4A5568]">
            {formatDate(msg.created_at)}
            {msg.subject ? ` · ${msg.subject}` : ""}
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm mb-4">
        <InfoRow icon={Mail} label="Email" value={msg.email} href={`mailto:${msg.email}`} />
        {msg.phone && (
          <InfoRow icon={Phone} label="Phone" value={msg.phone} href={`tel:${msg.phone}`} />
        )}
      </div>
      <p className="text-[#1A202C] leading-relaxed border-t border-[#E2E8F0] pt-4">
        {msg.message}
      </p>
    </article>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const content = (
    <>
      <Icon className="w-4 h-4 text-[#D32F2F] shrink-0" strokeWidth={2.5} />
      <div className="min-w-0">
        <div className="font-mono-label text-[#4A5568]">{label}</div>
        <div className="font-semibold text-[#0B132B] break-words">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex gap-3 items-start hover:text-[#D32F2F]">
      {content}
    </a>
  ) : (
    <div className="flex gap-3 items-start">{content}</div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
